import React, { useState, useEffect, useRef, useCallback, useContext, useMemo } from "react";
import { useAuth0 } from "./useAuth0";
import theme from "../theme";
import configs from "../../configs";
import { normalizeEffectiveEntitlements } from "../../api/SubscriptionsService";
import InitialLoading from "../InitialLoading";
import { deleteCookie } from "../utils";
import { setHttpOnlyCookie } from "../../cookieUtils";
import PropTypes from "prop-types";
import { dashboardApiClient } from "../../api/DashboardApiClient";
import { persistenceApiClient } from "../../api/PersistenceApiClient";
import { resolveRequestWorkspaceId } from "../../api/workspaceRequestScope.js";
import userPreferences from "../../api/UserPreferences";
import { handleInstantAccessTokenExpiry } from "../../api/instantAccessReauth.js";
import { captureReauthHintFromSession, resolveAuthMode, resolveInstantAccessSid } from "../../api/reauthHints";
import { clearAuthHint, writeAuthHint } from "../auth/authHint.js";
import {
  buildEditorLogoutRedirectUrl,
  buildInstantAccessRedirectUrl,
  isAllowedPostLogoutRedirectUrl
} from "../auth/xrhubAuthHandoff.js";

const LOCAL_STORE_KEY = "___hubs_store";
const PENDING_INVITATION_KEY = "pending_invitation_token";
const LOGOUT_REDIRECT_KEY = "logout_redirect";
const SELECTED_WORKSPACE_KEY = "selected_workspace_id";
const AUTH_MODE_KEY = "auth_mode";
const AUTH_MODE_INSTANT = "instant";
const INSTANT_ACCESS_SID_KEY = "instant_access_sid";
const INSTANT_ACCESS_USERNAME_KEY = "instant_access_username";
const INSTANT_ACCESS_SID_PATTERN = /^[A-Za-z0-9]{7}$/;

function getCreatorBaseUrl({
  locationOrigin = window.location.origin,
  creatorBasePath = configs.ROUTER_BASE_PATH
} = {}) {
  return `${locationOrigin}${creatorBasePath || ""}`;
}

export const AuthContext = React.createContext();

export const useAuth = () => useContext(AuthContext);

export function getDefaultWorkspace(authUser, workspaces = [], selectedWorkspace = null) {
  void authUser;

  if (!Array.isArray(workspaces) || workspaces.length === 0) {
    return null;
  }

  const defaultWorkspace = workspaces.find(workspace => workspace.is_default);
  if (defaultWorkspace) {
    return defaultWorkspace;
  }

  const selectedWorkspaceId = selectedWorkspace?.id;
  if (selectedWorkspaceId) {
    const selected = workspaces.find(workspace => workspace.id === selectedWorkspaceId);
    if (selected) {
      return selected;
    }
  }

  return workspaces[0] || null;
}

export function isWorkspaceSelectable(workspace) {
  return workspace?.selection_state === "selectable";
}

export function getLogoutReturnTo(authMode, instantAccessSid, options = {}) {
  const hasValidInstantSid = typeof instantAccessSid === "string" && INSTANT_ACCESS_SID_PATTERN.test(instantAccessSid);
  const locationOrigin = options.locationOrigin || window.location.origin;
  const creatorBasePath = Object.prototype.hasOwnProperty.call(options, "creatorBasePath")
    ? options.creatorBasePath
    : configs.ROUTER_BASE_PATH;
  const xrhubUrl = Object.prototype.hasOwnProperty.call(options, "xrhubUrl") ? options.xrhubUrl : configs.XRHUB_URL;

  if (authMode === AUTH_MODE_INSTANT && hasValidInstantSid) {
    return buildInstantAccessRedirectUrl({
      instantAccessSid,
      xrhubUrl,
      locationOrigin,
      creatorBasePath
    });
  }

  return getCreatorBaseUrl({ locationOrigin, creatorBasePath });
}

function getLogoutRedirectPath(authMode, instantAccessSid) {
  const hasValidInstantSid = typeof instantAccessSid === "string" && INSTANT_ACCESS_SID_PATTERN.test(instantAccessSid);
  return authMode === AUTH_MODE_INSTANT && hasValidInstantSid ? `/instant/${instantAccessSid}` : "";
}

export function getLogoutPlan(authMode, instantAccessSid, options = {}) {
  const locationOrigin = options.locationOrigin || window.location.origin;
  const creatorBasePath = Object.prototype.hasOwnProperty.call(options, "creatorBasePath")
    ? options.creatorBasePath
    : configs.ROUTER_BASE_PATH;
  const xrhubUrl = Object.prototype.hasOwnProperty.call(options, "xrhubUrl") ? options.xrhubUrl : configs.XRHUB_URL;
  const auth0ReturnTo = getCreatorBaseUrl({
    locationOrigin,
    creatorBasePath
  });
  const appRedirect = getLogoutReturnTo(authMode, instantAccessSid, options);
  const logoutRedirectPath = getLogoutRedirectPath(authMode, instantAccessSid);

  return {
    auth0ReturnTo,
    appRedirect: appRedirect !== auth0ReturnTo ? appRedirect : null,
    xrhubLogoutUrl: buildEditorLogoutRedirectUrl({
      redirectPath: logoutRedirectPath,
      xrhubUrl,
      locationOrigin,
      creatorBasePath
    })
  };
}

export const AuthContextProvider = function (props) {
  const { api, children } = props;

  const auth0 = useAuth0();
  const { loading, loginWithRedirect, getAccessTokenSilently, auth0Logout, loginWithPopup } = auth0;

  const isInstantAccessAuthenticated = resolveAuthMode(localStorage) === AUTH_MODE_INSTANT && api.isAuthenticated();
  const isAuthenticated = isInstantAccessAuthenticated || auth0.isAuthenticated;
  const hasAuthenticated = useRef(false);
  const store = api.getStore();

  const normalizeAuthUser = useCallback(user => {
    if (!user || typeof user !== "object") {
      return null;
    }

    const profileImageUrl = user?.profile_image_url || (typeof user?.picture === "string" ? user.picture : undefined);

    if (user?.profile_image_url === profileImageUrl) {
      return user;
    }

    return {
      ...user,
      profile_image_url: profileImageUrl
    };
  }, []);

  const [context, setContext] = useState({
    authUser: normalizeAuthUser(store?.authUser) || null,
    loginAuth0User: null,
    isAuthenticated: false,
    getAccessTokenSilently: () => {},
    loginWithRedirect: () => {},
    loginWithPopup: null,
    logout: null,
    authUserToolbarMenu: [],
    authenticate: null,
    isUserInformationLoaded: null,
    loading: true,
    refreshPermissions: null,
    workspaces: [],
    selectedWorkspace: null,
    setSelectedWorkspace: () => {}, // Placeholder
    workspaceRole: null,
    updateUser: user => {
      setContext(state => ({
        ...state,
        authUser: {
          ...state.authUser,
          ...user
        }
      }));
    }
  });

  // Keep UserPreferences synced with currently authenticated user
  useEffect(() => {
    userPreferences.setAuthUser(context.authUser);
  }, [context.authUser]);

  // Ref to store current context for getters to avoid stale closures/race conditions
  const contextRef = useRef(context);
  contextRef.current = context;

  // Helper function to compute workspace role
  const getWorkspaceRole = useCallback((workspace, userId) => {
    void userId;
    return workspace?.authorization?.role || null;
  }, []);

  // Get the preferred workspace for flows that need a default workspace:
  // true default workspace -> selected workspace -> first available workspace.
  const getDefaultWorkspaceFromContext = useCallback(() => {
    const authUser = contextRef.current?.authUser;
    const workspaces = contextRef.current?.workspaces || [];
    const selectedWorkspace = contextRef.current?.selectedWorkspace || null;
    return getDefaultWorkspace(authUser, workspaces, selectedWorkspace);
  }, []);

  // Get the user's default workspace ID (convenience function)
  const getDefaultWorkspaceId = useCallback(() => {
    const defaultWorkspace = getDefaultWorkspaceFromContext();
    return defaultWorkspace?.id || null;
  }, [getDefaultWorkspaceFromContext]);

  const setSelectedWorkspace = useCallback(
    workspace => {
      setContext(state => {
        const newState = {
          ...state,
          selectedWorkspace: workspace
        };
        return newState;
      });
      if (workspace) {
        localStorage.setItem(SELECTED_WORKSPACE_KEY, workspace.id);

        // Persist the selected Workspace in the platform (fire and forget, no await).
        if (api && workspace.id) {
          api.setCurrentWorkspace(workspace.id).catch(error => {
            // Silently handle errors - this is a non-blocking update.
            console.warn("Failed to set current Workspace:", error);
          });
        }
      } else {
        localStorage.removeItem(SELECTED_WORKSPACE_KEY);
      }
    },
    [api]
  );

  const fetchWorkspaces = useCallback(
    async (token, preSelectedId = null, lastWorkspaceId = null, retryCount = 0) => {
      try {
        const response = await fetch(`${configs.CUSTOM_API_URL}/workspaces`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!response.ok) {
          if (retryCount < 2) {
            console.warn(`Failed to fetch workspaces, retrying (${retryCount + 1}/2)...`);
            await new Promise(resolve => setTimeout(resolve, 1000));
            return fetchWorkspaces(token, preSelectedId, lastWorkspaceId, retryCount + 1);
          }
          throw new Error(`Failed to fetch workspaces: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        const workspaces = Array.isArray(data) ? data : data.data || [];

        if (!Array.isArray(workspaces) || workspaces.length === 0) {
          console.warn("No workspaces found for user");
          // If no workspaces found, inject a dummy default workspace
          const dummyWorkspace = {
            id: "default",
            name: "Default Workspace",
            is_default: true,
            type: "private"
          };
          setContext(state => ({
            ...state,
            workspaces: [dummyWorkspace]
          }));
          setSelectedWorkspace(dummyWorkspace);
          return;
        }

        setContext(state => ({
          ...state,
          workspaces
        }));

        // Handle selection logic...
        let workspaceToSelect = null;
        if (preSelectedId) {
          const candidate = workspaces.find(w => w.id === preSelectedId);
          if (isWorkspaceSelectable(candidate)) {
            workspaceToSelect = candidate;
          }
        }

        if (!workspaceToSelect) {
          const savedId = localStorage.getItem(SELECTED_WORKSPACE_KEY);
          if (savedId) {
            const candidate = workspaces.find(w => w.id === savedId);
            if (isWorkspaceSelectable(candidate)) {
              workspaceToSelect = candidate;
            }
          }
        }

        if (!workspaceToSelect && lastWorkspaceId) {
          const candidate = workspaces.find(w => w.id === lastWorkspaceId);
          if (isWorkspaceSelectable(candidate)) {
            workspaceToSelect = candidate;
          }
        }

        if (!workspaceToSelect) {
          // Prefer default workspace that is selectable
          workspaceToSelect =
            workspaces.find(w => w.is_default && isWorkspaceSelectable(w)) ||
            workspaces.find(isWorkspaceSelectable) ||
            workspaces[0];
        }

        if (workspaceToSelect) {
          setSelectedWorkspace(workspaceToSelect);
        } else if (workspaces[0]) {
          // Final fallback if for some reason none are selectable
          setSelectedWorkspace(workspaces[0]);
        }
      } catch (error) {
        console.error("Error fetching workspaces:", error);
        if (retryCount < 2) {
          console.warn(`Error fetching workspaces, retrying (${retryCount + 1}/2)...`);
          await new Promise(resolve => setTimeout(resolve, 1000));
          return fetchWorkspaces(token, preSelectedId, lastWorkspaceId, retryCount + 1);
        }

        // Final fallback on error
        const dummyWorkspace = {
          id: "default-error",
          name: "Default Workspace (Offline)",
          is_default: true,
          type: "private"
        };
        setContext(state => ({
          ...state,
          workspaces: [dummyWorkspace]
        }));
        setSelectedWorkspace(dummyWorkspace);
      }
    },
    [setSelectedWorkspace]
  );

  const logout = useCallback(
    (options = {}) => {
      const authMode = resolveAuthMode(localStorage);
      const instantAccessSid = resolveInstantAccessSid(localStorage);
      const logoutPlan = getLogoutPlan(authMode, instantAccessSid);

      setContext(state => ({ ...state, authUser: null, workspaces: [], selectedWorkspace: null }));

      const store = api.getStore();

      if (store) {
        store.credentials = { email: "", token: "" };
        store.authUser = null;
        localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(store));
      }

      captureReauthHintFromSession(localStorage);
      clearAuthHint();
      localStorage.removeItem("auth0token");
      localStorage.removeItem(AUTH_MODE_KEY);
      localStorage.removeItem(INSTANT_ACCESS_SID_KEY);
      localStorage.removeItem(INSTANT_ACCESS_USERNAME_KEY);
      deleteCookie("auth0");
      // Clear the HTTP-only cookie by setting it to expire immediately
      setHttpOnlyCookie("auth0token", "", -1);

      // Store error message in localStorage to display after redirect
      if (options.errorType && options.errorMessage) {
        localStorage.setItem(
          "auth_error",
          JSON.stringify({
            type: options.errorType,
            message: options.errorMessage
          })
        );
      }

      if (logoutPlan.xrhubLogoutUrl) {
        try {
          window.location.assign(logoutPlan.xrhubLogoutUrl);
          return;
        } catch (_error) {
          // Fall through to the direct Auth0 or bridge logout path.
        }
      }

      if (logoutPlan.appRedirect) {
        localStorage.setItem(LOGOUT_REDIRECT_KEY, logoutPlan.appRedirect);
      }

      auth0Logout({
        logoutParams: {
          returnTo: logoutPlan.auth0ReturnTo
        }
      });
    },
    [api, auth0Logout]
  );

  const onAuthenticatedMetaverse = () => {
    return new Promise(resolve => {
      // Dispatch the event or perform other asynchronous tasks
      window.dispatchEvent(new CustomEvent("metaverse-authentication-refreshed"));

      // If there are other async operations, you can add them here
      // For example, simulating async work with setTimeout:
      setTimeout(() => {
        resolve();
      }, 100); // Delay of 100ms
    });
  };

  const authenticate = useCallback(
    async (email, cb, token) => {
      const abortController = new AbortController();
      if (!email) return;

      try {
        await api.authenticate(email, abortController.signal, token);
        await cb();
      } catch (error) {
        console.error("Authentication error:", error);
        // Optionally handle errors or notify the user here
      }
    },
    [api]
  );

  const loginAuth0User = useCallback(
    async (credentials, forceRefresh = false) => {
      if (hasAuthenticated.current && !forceRefresh) return;
      try {
        const token = await getAccessTokenSilently({
          audience: configs.AUTH0_AUDIENCE,
          scope: "openid profile email offline_access"
        });
        let user = null;
        let authUser = null;

        if (token) {
          user = await api.getAuth0User(token, forceRefresh);
        }

        if (token && !user) {
          if (!forceRefresh) {
            logout();
          }
          return;
        }

        if (user) {
          authUser = normalizeAuthUser({
            id: user?.id,
            name: user?.name || user?.email?.split("@")[0] || credentials?.email?.split("@")[0] || "",
            email: user?.email || credentials?.email,
            user_color: user?.user_color || theme.primaryColor,
            stripe_id: user?.stripe_id,
            onboarding_completed: user?.onboarding_completed,
            profile_image_url: user?.profile_image_url,
            avatar: user?.avatar || "",
            preferences: user?.preferences || {},
            created_at: user?.created_at,
            permissions: Array.isArray(user?.permissions) ? user.permissions : [],
            roles: Array.isArray(user?.roles) ? user.roles : [],
            last_workspace_id: user?.last_workspace_id || null,
            entitlements: user?.entitlements ? normalizeEffectiveEntitlements(user.entitlements) : null
          });

          if (token) {
            writeAuthHint(authUser);
            // Fetch workspaces after authentication
            // Pass last_workspace_id for cross-device sync when localStorage is empty
            await fetchWorkspaces(token, null, authUser.last_workspace_id);
          }

          if (!credentials?.token || credentials.email !== user.email) {
            await authenticate(user.email, onAuthenticatedMetaverse, token);
          }
        }

        // Process pending invitation if exists
        const pendingInvitationToken = localStorage.getItem(PENDING_INVITATION_KEY);
        const pendingInvitationType = localStorage.getItem("pending_invitation_type");
        if (pendingInvitationToken && authUser) {
          try {
            // Use invitation type from URL to call the correct endpoint
            let result;
            let isWorkspaceInvitation = false;

            if (pendingInvitationType === "workspace") {
              result = await api.acceptWorkspaceInvitation(pendingInvitationToken);
              isWorkspaceInvitation = true;
            } else {
              // Default to user invitation for 'user', 'group', or missing type (backward compatibility)
              result = await api.claimUserInvitation(pendingInvitationToken);
            }

            // Clean up localStorage
            localStorage.removeItem(PENDING_INVITATION_KEY);
            localStorage.removeItem("pending_invitation_type");

            // Refresh user data to get updated permissions/roles/limits
            const refreshedUser = await api.getAuth0User(token, true);
            if (refreshedUser) {
              authUser = normalizeAuthUser({
                id: refreshedUser?.id,
                name: refreshedUser?.name || refreshedUser?.email?.split("@")[0] || "",
                email: refreshedUser?.email,
                user_color: refreshedUser?.user_color || theme.primaryColor,
                stripe_id: refreshedUser?.stripe_id,
                onboarding_completed: refreshedUser?.onboarding_completed,
                profile_image_url: refreshedUser?.profile_image_url,
                avatar: refreshedUser?.avatar || "",
                preferences: refreshedUser?.preferences || {},
                created_at: refreshedUser?.created_at,
                permissions: Array.isArray(refreshedUser?.permissions) ? refreshedUser.permissions : [],
                roles: Array.isArray(refreshedUser?.roles) ? refreshedUser.roles : [],
                last_workspace_id: refreshedUser?.last_workspace_id || null
              });
            }

            // If workspace invitation was accepted, refresh workspaces list
            if (isWorkspaceInvitation && result.workspace_id) {
              // Refresh workspaces to include the newly joined workspace
              const workspaceId = result.workspace_id;
              await fetchWorkspaces(token, workspaceId, refreshedUser?.last_workspace_id || null);
            }

            // Show success notification
            setTimeout(() => {
              if (window.notify) {
                if (isWorkspaceInvitation) {
                  // Workspace invitation success
                  window.notify({
                    captionTitle: "Workspace Invitation Accepted",
                    caption: result.message || "You've been added to the workspace!",
                    intent: "success",
                    timeout: 5000
                  });
                } else {
                  // User invitation success (existing logic)
                  const appliedItems = [];
                  if (result.applied?.group) appliedItems.push(`Group: ${result.applied.group.name}`);
                  if (result.applied?.role) appliedItems.push(`Role: ${result.applied.role}`);
                  if (result.applied?.subscription_template)
                    appliedItems.push(`Plan: ${result.applied.subscription_template.name}`);

                  window.notify({
                    captionTitle: "Invitation Accepted",
                    caption: appliedItems.length > 0 ? appliedItems.join(" • ") : "Welcome!",
                    intent: "success",
                    timeout: 5000
                  });
                }
              }
            }, 500);
          } catch (invitationError) {
            console.warn("Failed to claim invitation:", invitationError.message);
            // Clean up localStorage
            localStorage.removeItem(PENDING_INVITATION_KEY);
            localStorage.removeItem("pending_invitation_type");

            // Show error notification
            setTimeout(() => {
              if (window.notify) {
                window.notify({
                  captionTitle: "Invitation Failed",
                  caption: invitationError.message || "Could not process the invitation",
                  intent: "error",
                  timeout: 5000
                });
              }
            }, 500);
          }
        }

        if (api) {
          api.emit("authentication-changed", true);
        }

        setContext(state => ({
          ...state,
          authUser,
          isAuthenticated: !!authUser,
          loading: false
        }));

        if (!forceRefresh) {
          hasAuthenticated.current = true; // Set ref to true to indicate the user has been authenticated
        }
      } catch (error) {
        console.error(error);

        // Handle banned user error (403)
        if (error.status === 403 || error.code === "ACCOUNT_SUSPENDED") {
          const errorMessage = error.message || "Your account has been suspended. Please contact an administrator.";

          // Logout with error params - Auth0 will redirect to login with error message
          logout({
            errorType: "suspended",
            errorMessage
          });
          return;
        }

        if (!forceRefresh) {
          setContext(state => ({
            ...state,
            loading: false,
            isAuthenticated: false,
            authUser: null
          }));
        }
      }
    },
    [getAccessTokenSilently, logout, api, authenticate, fetchWorkspaces, normalizeAuthUser]
  );

  const refreshPermissions = useCallback(async () => {
    try {
      const currentStore = api.getStore();
      const credentials = currentStore?.credentials;
      await loginAuth0User(credentials, true);
      return true;
    } catch (error) {
      console.error("Failed to refresh permissions:", error);
      return false;
    }
  }, [loginAuth0User, api]);

  const popUpLogin = useCallback(async () => {
    await loginWithPopup({
      authorizationParams: {
        audience: configs.AUTH0_AUDIENCE,
        scope: "openid profile email offline_access"
      }
    });
    const currentStore = api.getStore();
    const credentials = currentStore?.credentials;
    hasAuthenticated.current = false;
    await loginAuth0User(credentials);
  }, [loginWithPopup, api, loginAuth0User]);

  const reauthenticateForDashboardCanary = useCallback(async () => {
    const authMode = resolveAuthMode(localStorage);

    if (authMode === AUTH_MODE_INSTANT) {
      const didRedirectInstantAccess = handleInstantAccessTokenExpiry({
        storage: localStorage,
        location: window.location,
        routerBasePath: configs.ROUTER_BASE_PATH,
        clearAuthCookie: () => setHttpOnlyCookie("auth0token", "", -1),
        xrhubUrl: configs.XRHUB_URL
      });

      if (didRedirectInstantAccess) {
        return false;
      }
      return false;
    }

    try {
      const didSilentReauth = await api.reauthenticate({ withPopup: false });
      if (didSilentReauth) {
        return true;
      }
    } catch (_silentReauthError) {
      // Continue to interactive fallback.
    }

    try {
      return await api.reauthenticate({ withPopup: true });
    } catch (_interactiveReauthError) {
      return false;
    }
  }, [api]);

  useEffect(() => {
    if (api) {
      api.loginWithPopup = popUpLogin;
    }
  }, [api, popUpLogin]);

  useEffect(() => {
    if (dashboardApiClient?.setReauthHandler) {
      dashboardApiClient.setReauthHandler(reauthenticateForDashboardCanary);
    }
    if (persistenceApiClient?.setReauthHandler) {
      persistenceApiClient.setReauthHandler(reauthenticateForDashboardCanary);
    }

    return () => {
      if (dashboardApiClient?.setReauthHandler) {
        dashboardApiClient.setReauthHandler(null);
      }
      if (persistenceApiClient?.setReauthHandler) {
        persistenceApiClient.setReauthHandler(null);
      }
    };
  }, [reauthenticateForDashboardCanary]);

  const authUserToolbarMenu = useMemo(
    () => [
      {
        name: "Logout",
        action: () => logout()
      }
    ],
    [logout]
  );

  // Compute workspace role based on current user and selected workspace
  const authUserId = context.authUser?.id;
  const workspaceRole = useMemo(() => {
    return getWorkspaceRole(context.selectedWorkspace, authUserId);
  }, [context.selectedWorkspace, authUserId, getWorkspaceRole]);

  const checkAuthUser = useCallback(async () => {
    const currentStore = api.getStore();
    const credentials = currentStore?.credentials;
    await loginAuth0User(credentials);
  }, [api, loginAuth0User]);

  // Handle logout redirect after Auth0 redirects back
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      const logoutRedirect = localStorage.getItem(LOGOUT_REDIRECT_KEY);
      if (logoutRedirect) {
        // Remove the redirect flag
        localStorage.removeItem(LOGOUT_REDIRECT_KEY);

        if (
          isAllowedPostLogoutRedirectUrl(logoutRedirect, {
            locationOrigin: window.location.origin,
            xrhubUrl: configs.XRHUB_URL
          })
        ) {
          window.location.assign(logoutRedirect);
          return;
        }

        console.warn("Invalid logout redirect URL:", logoutRedirect);
      }
    }
  }, [loading, isAuthenticated]);

  // Configure Api, DashboardApiClient, and PersistenceApiClient to get workspace from context instead of localStorage
  useEffect(() => {
    const workspaceGetter = () => contextRef.current?.selectedWorkspace || null;
    const userIdGetter = () => contextRef.current?.authUser?.id || null;
    const workspaceIdGetter = () => resolveRequestWorkspaceId(api, contextRef.current?.selectedWorkspace?.id || null);
    const defaultWorkspaceIdGetter = () => {
      return getDefaultWorkspaceFromContext()?.id || null;
    };

    // Configure Api class with full workspace getter
    if (api && api.setWorkspaceGetter) {
      api.setWorkspaceGetter(workspaceGetter, userIdGetter);
    }

    // Configure DashboardApiClient singleton (still uses ID getter for compatibility)
    if (dashboardApiClient && dashboardApiClient.setWorkspaceIdGetter) {
      dashboardApiClient.setWorkspaceIdGetter(workspaceIdGetter);
    }

    // Configure PersistenceApiClient singleton
    if (persistenceApiClient && persistenceApiClient.setWorkspaceIdGetter) {
      persistenceApiClient.setWorkspaceIdGetter(workspaceIdGetter);
    }

    // Configure PersistenceApiClient with default workspace getter for avatar operations
    if (persistenceApiClient && persistenceApiClient.setDefaultWorkspaceIdGetter) {
      persistenceApiClient.setDefaultWorkspaceIdGetter(defaultWorkspaceIdGetter);
    }
  }, [api, getDefaultWorkspaceFromContext]);

  // Only runs once when the component is mounted
  useEffect(() => {
    if (!loading) {
      setContext(state => ({
        ...state,
        isAuthenticated,
        loginWithPopup: popUpLogin,
        loginWithRedirect,
        loginAuth0User,
        getAccessTokenSilently,
        auth0Logout,
        logout,
        authUserToolbarMenu,
        refreshPermissions,
        authenticate,
        setSelectedWorkspace
      }));

      checkAuthUser();
    }
  }, [
    loading,
    refreshPermissions,
    isAuthenticated,
    popUpLogin,
    loginWithRedirect,
    loginAuth0User,
    getAccessTokenSilently,
    auth0Logout,
    logout,
    authUserToolbarMenu,
    authenticate,
    checkAuthUser,
    setSelectedWorkspace
  ]);

  const contextValue = useMemo(
    () => ({
      ...context,
      workspaceRole,
      getWorkspaceRole,
      getDefaultWorkspace: getDefaultWorkspaceFromContext,
      getDefaultWorkspaceId
    }),
    [context, workspaceRole, getWorkspaceRole, getDefaultWorkspaceFromContext, getDefaultWorkspaceId]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      {!context.loading ? <>{children}</> : <InitialLoading message="Loading..." api={api} />}
    </AuthContext.Provider>
  );
};

AuthContextProvider.propTypes = {
  api: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};
